import { Bell, Component, LayoutDashboard, LogOut, Users } from 'lucide-react'
import { Button, Container, Nav, Offcanvas } from 'react-bootstrap'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { useState } from 'react'

import { useLogoutUserMutation } from '../app/authApiSlice'
import Footer from '../components/Footer'
import { useFetchAdminQuery } from '../app/adminApiSlice'
import SkeletonBody from './skeletonLayout/SkeletonBody'
import Header from '../components/Header'

const AdminLayout = () => {
  const { pathname } = useLocation()
  const [logoutUser] = useLogoutUserMutation()
  const { data, isSuccess, isLoading, isError, error } = useFetchAdminQuery()

  const [showMenu, setShowMenu] = useState(false)
  const handleToggleMenu = () => setShowMenu(!showMenu)

  const showCurrentPage = path => (pathname.startsWith(path) ? 'text-primary' : 'text-muted custom-hover')

  const onLogoutClicked = () => {
    logoutUser()
  }

  if (isLoading) return <SkeletonBody />
  if (isError) {
    console.error(error || 'An error occurred')
    return (
      <Container
        className="d-flex flex-column align-items-center mt-4 position-relative"
        style={{ paddingTop: '4.8375rem' }}
      >
        <div className="d-flex flex-column align-items-center mt-5">
          <h1 className="mb-4 fs-2">Failed to load data</h1>
          <p className="mb-4"> Please log out and try again.</p>
          <Button className="mt-2 px-5 rounded-3" variant="dark" onClick={onLogoutClicked}>
            Log Out
          </Button>
        </div>
      </Container>
    )
  }
  if (!isSuccess || !data) return null

  const { notifications } = data

  return (
    <>
      <Header handleToggleMenu={handleToggleMenu} showMenu={showMenu} />

      {/* Sidebar */}
      <Offcanvas
        className="d-flex flex-column pt-5 px-2 px-sm-3 border-end position-fixed bg-white no-scrollbar off-canvas"
        style={{ width: '260px', top: '4.8375rem', bottom: '0', zIndex: '1042' }}
        show={showMenu}
        onHide={handleToggleMenu}
        responsive="lg"
      >
        <h4 className="mb-4 fw-bold px-4">Admin Panel</h4>
        <Nav className="flex-grow-1 d-flex flex-column">
          <Link
            to="/admin"
            className={`d-flex align-items-center px-4 mb-2 rounded text-decoration-none py-2 fw-semibold ${pathname === '/admin' ? 'text-primary' : 'text-muted custom-hover'}`}
            onClick={handleToggleMenu}
          >
            <LayoutDashboard className="me-2" strokeWidth={1.75} />
            Dashboard
          </Link>
          <Link
            to="/admin/members"
            className={`d-flex align-items-center px-4 mb-2 rounded text-decoration-none py-2 fw-semibold ${showCurrentPage('/admin/members')}`}
            onClick={handleToggleMenu}
          >
            <Users className="me-2" strokeWidth={1.75} />
            Members
          </Link>
          <Link
            to="/admin/clubs"
            className={`d-flex align-items-center px-4 mb-2 rounded text-decoration-none py-2 fw-semibold ${showCurrentPage('/admin/clubs')}`}
            onClick={handleToggleMenu}
          >
            <Component className="me-2" strokeWidth={1.75} />
            Clubs
          </Link>
          <Link
            to="/admin/notifications"
            className={`px-4 mb-2 rounded text-decoration-none fw-semibold ${showCurrentPage('/admin/notifications')}`}
            onClick={handleToggleMenu}
          >
            <div className="d-flex align-items-center">
              <div className="position-relative py-2">
                <Bell className="me-2" strokeWidth={1.75} />
                {notifications > 0 && <div className="notifications">{notifications}</div>}
              </div>
              <span className="d-inline">Notifications</span>
            </div>
          </Link>
          <hr className="text-muted mx-2" />
          <Link
            variant="link"
            className="d-flex align-items-center text-decoration-none py-2 px-4 my-2 rounded text-muted fw-semibold text-start custom-hover"
            onClick={onLogoutClicked}
          >
            <LogOut className="me-2" strokeWidth={1.75} />
            Logout
          </Link>
          <Link
            to="https://www.gymnasticbritannia.co.uk/privacy"
            className="d-flex align-items-center px-4 mt-auto mb-4 rounded text-decoration-none text-dark pt-2 opacity-50 fs-8"
          >
            Privacy Policy
          </Link>
        </Nav>
      </Offcanvas>

      {/* Main Content */}
      <div className="d-flex flex-column flex-grow-1 position-relative main-layout mw-100">
        <div className="h-100 d-flex flex-column position-relative">
          <div className=" py-5 px-3  p-sm-5 d-flex flex-column align-items-center h-100">
            <Outlet />
          </div>
          <Footer />
        </div>
      </div>
    </>
  )
}

export default AdminLayout
